/**
 * The only door the rest of the game has into boat-to-boat collision.
 *
 * `src/sim/modules/load.ts` discovers `boatCollision/install.ts` with a glob.
 * Deleting that folder leaves this host with no module: `separateFleet` walks
 * each boat through terrain alone, and the race still runs.
 */
import type { CollisionContact } from '../collision.ts';
import { CRASH_HARSHNESS, TURN_TAX } from '../constants.ts';
import type { CrashImpact } from '../crash.ts';
import type { HullDimensions } from '../hullSize.ts';
import type { MapDocument } from '../map/types.ts';
import type { MovePath } from '../path.ts';
import { advanceThroughTerrain } from '../terrainRun.ts';
import type { BoatState, Vec2 } from '../types.ts';

export type FleetSeat = {
  id: number;
  boat: BoatState;
  dimensions: HullDimensions;
  /** Path this seat means to swim. Null leaves it sitting where it is. */
  intended: MovePath | null;
  /** Where `intended` puts it when nothing interrupts. Omitted for a seat that is not moving. */
  landedIfClear?: BoatState;
};

export type FleetRun = {
  id: number;
  /** Landed state for the whole turn, shoves included. */
  boat: BoatState;
  /** Everything this hull swam: its own move, then any drift it was pushed into. */
  path: MovePath | null;
  impactPath: MovePath | null;
  clipped: boolean;
  crash: CrashImpact | null;
  crossingDeg: number | null;
  contacts: CollisionContact[];
  reported: CollisionContact[];
  /** True when another boat changed where this one ended up. */
  shoved: boolean;
};

/** How hard a hit was for the boat at fault: a nudge is free, medium and hard slow it. */
export type BoatHitSeverity = 'nudge' | 'medium' | 'hard';

/** One hull hitting another. The picture and the log read this; the sweep stays in the module. */
export type BoatHit = {
  fromId: number;
  intoId: number;
  point: Vec2;
  /** Unit, pointing from `fromId` toward `intoId`. */
  normal: Vec2;
  /** Turn fraction in `[0, 1]` when the hulls met. */
  at: number;
  /** Normal impulse magnitude. Nothing spends this yet; the penalty reads `severity`. */
  impulse: number;
  /** Closing speed along the normal, in path pixels per turn. */
  closingSpeed: number;
  /** Where the blow landed on the striking hull: 0 at the transom, 1 at the bow. */
  fromAlong: number;
  /** Where it landed on the struck hull, same scale. */
  intoAlong: number;
  /** Seat that was closing the gap. Null when both were — a head-on. */
  faultId: number | null;
  /**
   * The seat at fault ran into the other's rear quarter while heading the same way: braking too
   * late. Such a hit is at least `medium`, however slow.
   */
  rearEnd: boolean;
  /**
   * From how fast the hulls were closing, each at its way: a parked seat counts at the speed of
   * its last move, so a brush between boats at the same speed is a nudge. The seat at fault
   * (both, on a head-on) pays it on landing.
   */
  severity: BoatHitSeverity;
};

export type FleetSeparation = {
  runs: FleetRun[];
  hits: BoatHit[];
};

type BoatCollisionModule = {
  separate(
    map: MapDocument,
    seats: readonly FleetSeat[],
    crashHarshness: number,
    turnTax: number,
  ): FleetSeparation;
  formatHit(hit: BoatHit, name: string, struck: boolean): string;
  /** Closing speed (px/turn, each hull at its way) below which a hit is a free nudge. */
  nudgeClosingPx: number;
};

let api: BoatCollisionModule | null = null;
/** This race wants hull hits. Ignored when no module registered. */
let enabled = true;

export function registerBoatCollision(next: BoatCollisionModule): void {
  api = next;
}

export function boatCollisionInstalled(): boolean {
  return api !== null;
}

/** Hot Seat On/Off. A missing module stays off no matter what this is set to. */
export function setBoatCollisionsEnabled(next: boolean): void {
  enabled = next;
}

export function boatCollisionsActive(): boolean {
  return api !== null && enabled;
}

/**
 * Closing speed below which a hull hit costs nothing, for planners that want to know which
 * contacts are free. Infinity when hulls pass through each other (module absent or switched off).
 */
export function boatNudgeClosingPx(): number {
  return boatCollisionsActive() && api ? api.nudgeClosingPx : Infinity;
}

export function formatBoatHit(hit: BoatHit, name: string, struck: boolean): string {
  return api?.formatHit(hit, name, struck) ?? '';
}

/**
 * Boat-to-boat resolution for one shared turn. With the module absent or this
 * race switched off, each seat only meets the map.
 */
export function separateFleet(
  map: MapDocument,
  seats: readonly FleetSeat[],
  crashHarshness: number = CRASH_HARSHNESS,
  turnTax: number = TURN_TAX,
): FleetSeparation {
  if (boatCollisionsActive() && seats.length > 1 && api) {
    return api.separate(map, seats, crashHarshness, turnTax);
  }

  return {
    hits: [],
    runs: seats.map((seat) => terrainOnly(map, seat, crashHarshness, turnTax)),
  };
}

function terrainOnly(
  map: MapDocument,
  seat: FleetSeat,
  crashHarshness: number,
  turnTax: number,
): FleetRun {
  const landedIfClear = seat.landedIfClear ?? seat.boat;
  const run = seat.intended
    ? advanceThroughTerrain(
        map,
        seat.boat,
        seat.intended,
        landedIfClear,
        crashHarshness,
        turnTax,
      )
    : null;

  if (!run) {
    return {
      id: seat.id,
      boat: landedIfClear,
      path: null,
      impactPath: null,
      clipped: false,
      crash: null,
      crossingDeg: null,
      contacts: [],
      reported: [],
      shoved: false,
    };
  }

  return {
    id: seat.id,
    boat: run.boat,
    path: run.path,
    impactPath: run.impactPath,
    clipped: run.clipped,
    crash: run.crash,
    crossingDeg: run.crossingDeg,
    contacts: run.contacts,
    reported: run.reported,
    shoved: false,
  };
}
